/**
 * Hook para listar organizacoes do usuario logado que possuem permissao
 * `chamados:read` (alguma das tenants associadas).
 *
 * Usado pelo seletor de escopo do modulo de chamados (TASK-2469/2470).
 *
 * Endpoint: GET /v1/me/organizations/with-chamados-access (TASK-2460)
 *
 * Spec: §9.2.3 do CHAMADOS_VISIBILIDADE_SPEC.md
 * Gap G-N13 (§19.2): staleTime reduzido de 5min para 60s para frescor.
 *
 * Diferente de `useOrganizations` (lista global de organizacoes para admins) —
 * este filtra apenas o que o usuario logado pode acessar.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface UserOrganization {
  id: number
  name: string
  slug: string
  tenant_count: number
}

const QUERY_KEY = ['user', 'organizations', 'with-chamados-access'] as const

export function useUserOrganizations() {
  return useQuery<UserOrganization[]>({
    queryKey: [...QUERY_KEY],
    queryFn: async () => {
      const res = await api.get<UserOrganization[]>('/v1/me/organizations/with-chamados-access')
      return res.data
    },
    staleTime: 60 * 1000, // 60s — gap G-N13 (era 5min)
  })
}

/**
 * Helper para invalidar a query de organizacoes do usuario.
 * Chamar apos mutacoes que alteram membership do usuario em organizacoes.
 */
export function useInvalidateUserOrganizations() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: [...QUERY_KEY] })
}
